﻿import type { Angle } from "./Angle";
import { CoordinateSpace, type ICoordinateSpace, type InFullSizeImage, type InTransformed } from "./CoordinateSpace";
import type { IPoint, IPointInFullSizeImage } from "./Point";
import type { ISize, ISizeInFullSizeImage } from "./Size";
import type { ITransformation } from "./Transformation";

export interface IEllipse<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly coordinateSpace: TCoordinateSpace;
	readonly center: IPoint<TCoordinateSpace>;
	readonly size: ISize<TCoordinateSpace>;
	readonly rotation: Angle;

	readonly middleLeft: IPoint<TCoordinateSpace>;
	readonly middleRight: IPoint<TCoordinateSpace>;
	readonly topMiddle: IPoint<TCoordinateSpace>;
	readonly bottomMiddle: IPoint<TCoordinateSpace>;
}

export abstract class Ellipse<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements IEllipse<TCoordinateSpace> {
	public readonly coordinateSpace: TCoordinateSpace;
	public readonly center: IPoint<TCoordinateSpace>;
	public readonly size: ISize<TCoordinateSpace>;
	public readonly rotation: Angle;

	private _middleLeft: IPoint<TCoordinateSpace> | null;
	private _middleRight: IPoint<TCoordinateSpace> | null;
	private _topMiddle: IPoint<TCoordinateSpace> | null;
	private _bottomMiddle: IPoint<TCoordinateSpace> | null;

	protected constructor(coordinateSpace: TCoordinateSpace, center: IPoint<TCoordinateSpace>, size: ISize<TCoordinateSpace>, rotation: Angle) {
		this.coordinateSpace = coordinateSpace;
		this.center = center;
		this.size = size;
		this.rotation = rotation;

		this._middleLeft = null;
		this._middleRight = null;
		this._topMiddle = null;
		this._bottomMiddle = null;
	}

	get middleLeft(): IPoint<TCoordinateSpace> {
		const { center, size, rotation } = this;

		return this._middleLeft ?? (this._middleLeft = center.coordinateSpace.createPoint(center.x - size.width / 2, center.y).rotate(center, rotation));
	}

	get middleRight(): IPoint<TCoordinateSpace> {
		const { center, size, rotation } = this;

		return this._middleRight ?? (this._middleRight = center.coordinateSpace.createPoint(center.x + size.width / 2, center.y).rotate(center, rotation));
	}

	get topMiddle(): IPoint<TCoordinateSpace> {
		const { center, size, rotation } = this;

		return this._topMiddle ?? (this._topMiddle = center.coordinateSpace.createPoint(center.x, center.y - size.height / 2).rotate(center, rotation));
	}

	get bottomMiddle(): IPoint<TCoordinateSpace> {
		const { center, size, rotation } = this;

		return this._bottomMiddle ?? (this._bottomMiddle = center.coordinateSpace.createPoint(center.x, center.y + size.height / 2).rotate(center, rotation));
	}
}

export interface IEllipseInFullSizeImage extends IEllipse<InFullSizeImage> {}

export class EllipseInFullSizeImage extends Ellipse<InFullSizeImage> implements IEllipseInFullSizeImage {
	constructor(center: IPointInFullSizeImage, size: ISizeInFullSizeImage, rotation: Angle) {
		super(CoordinateSpace.inFullSizeImage, center, size, rotation);
	}
}

/**
 * A Vector in the coordinate space of the transformed full size image
 */
export class EllipseInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Ellipse<InTransformed<TCoordinateSpace>> {
	constructor(
		originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>,
		transformation: ITransformation<TCoordinateSpace>,
		center: IPoint<InTransformed<TCoordinateSpace>>,
		size: ISize<InTransformed<TCoordinateSpace>>,
		rotation: Angle,
	) {
		super(CoordinateSpace.inTransformed(originalCoordinateSpace, transformation), center, size, rotation);
	}
}
